import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { Button, Menu, MenuItem, MenuProps } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DataObjectIcon from '@mui/icons-material/DataObject';
import Quill from 'quill';
import Embed from 'quill/blots/embed';
import Block from 'quill/blots/block';
import styles from '../../style/editor.module.css';
import { styled } from '@mui/material/styles';
import { getRandomInt } from '../../util/NumberUtil';
import { escapeHtml, splitMergeTags, stripGuards, toStoredHtml, toStoredText } from './editorHtml';
import Delta from 'quill-delta';

type Tag = { value: string; title: string };

class TemplateMarker extends Embed {
  static blotName = 'TemplateMarker';
  static tagName = 'span';
  // https://stackoverflow.com/questions/69771687/quill-after-appendchild-is-not-a-function
  static className = 'template-class';

  static create(value: string) {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('class', styles.badge);
    node.innerHTML = value;
    return node;
  }

  static value(domNode: HTMLElement) {
    return stripGuards(domNode.textContent || '');
  }

  // Quill 2 uses this for getSemanticHTML(): emit the bare {{Tag}} the backend templates against,
  // rather than the embed's internal markup (whose guard characters turn into spaces on re-paste).
  html() {
    return escapeHtml(TemplateMarker.value(this.domNode as HTMLElement));
  }
}

Quill.register({
  'formats/TemplateMarker': TemplateMarker,
});

// Typing a space straight after a merge tag that ends a line leaves the caret before the space
// (browser contenteditable quirk around Quill's embed guards), so the next letters land in front of it.
// Insert the space ourselves and put the caret after it. It's a non-breaking space so the browser
// doesn't collapse it at the end of the line; toStoredHtml/toStoredText turn it back into a normal space.
// Defined at module level so the handler keeps the same identity across renders (react-quill
// re-creates the editor when `modules` changes).
const keyboardBindings = {
  spaceAfterMergeTag: {
    key: ' ',
    collapsed: true,
    handler(this: { quill: Quill }, range: { index: number }) {
      const [leaf] = this.quill.getLeaf(range.index);
      if (!(leaf instanceof TemplateMarker)) {
        return true; // let Quill handle it normally
      }
      this.quill.insertText(range.index, '\u00a0', 'user');
      this.quill.setSelection(range.index + 1, 0, 'user');
      return false;
    },
  },
};

// Any {{tag}} that comes into the editor as text, from a saved template or a paste, becomes a merge tag
// embed, so tags always look the same (blue, italic) whether they were inserted, loaded or pasted.
const mergeTagMatcher = (_node: Node, delta: Delta) =>
  new Delta(
    delta.ops.flatMap(op =>
      typeof op.insert !== 'string'
        ? [op]
        : splitMergeTags(op.insert).map(part =>
            typeof part === 'string' ? { insert: part, attributes: op.attributes } : { insert: { TemplateMarker: part.tag } },
          ),
    ),
  );

const clipboard = { matchers: [[Node.TEXT_NODE, mergeTagMatcher]] };

// Use <div> rather than <p> for lines, so emails keep the spacing users see in the editor
Block.tagName = 'div';
Quill.register(Block, true);

const StyledMenu = styled((props: MenuProps) => <Menu elevation={0} {...props} />)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
  },
  '& .MuiMenuItem-root': {
    fontSize: '14px',
  },
}));

type EditorProps = {
  isHtml: boolean;
  initialState: string;
  tags: Tag[];
  onContentUpdate: Function;
  showToolbar?: boolean;
  placeholder?: string;
};

const insertMergeTag = (quill: Quill, mergeTag: Tag): void => {
  const selection = quill.getSelection(true);
  const index = selection ? selection.index : 0;

  quill.insertEmbed(index, 'TemplateMarker', '{{' + mergeTag.value + '}}', 'user');
  quill.insertText(index + 1, ' ', 'user');
  quill.setSelection(index + 2, 0); // after the tag and the space
};

export default function Editor(props: EditorProps) {
  let { isHtml, initialState, tags, onContentUpdate, showToolbar, placeholder } = props;
  const [content, setContent] = useState<string>(initialState);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const quillRef = useRef<ReactQuill | null>(null);
  const templateMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    let editor = quillRef.current?.getEditor();
    if (editor) {
      // Goes through the clipboard, so mergeTagMatcher turns the template's {{tags}} into merge tags.
      editor.clipboard.dangerouslyPasteHTML(initialState, 'user');
    }
  }, [quillRef]);

  const toolbarId = useMemo(() => 'toolbar-' + getRandomInt(9999999), [isHtml]);

  const modules = {
    toolbar: showToolbar ? '#' + toolbarId : false,
    keyboard: { bindings: keyboardBindings },
    clipboard,
  };

  const formats = isHtml
    ? ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'indent', 'TemplateMarker']
    : ['TemplateMarker'];

  const handleTemplateMarkerButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleTemplateMarkerDropdownClose = () => {
    setAnchorEl(null);
  };

  const addTagButton = (
    <>
      <Button
        sx={{}}
        className="ql-template"
        id="template-marker-button"
        variant="outlined"
        disableElevation
        onClick={handleTemplateMarkerButtonClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        <DataObjectIcon />
      </Button>
      <StyledMenu id="template-marker-menu" open={templateMenuOpen} onClose={handleTemplateMarkerDropdownClose} anchorEl={anchorEl}>
        {tags.map(tag => {
          return (
            <MenuItem
              key={tag.value}
              id={tag.value}
              onClick={() => {
                if (quillRef.current) {
                  insertMergeTag(quillRef.current?.getEditor(), tag);
                }
                handleTemplateMarkerDropdownClose();
              }}
            >
              {tag.title}
            </MenuItem>
          );
        })}
      </StyledMenu>
    </>
  );

  const toolBar = showToolbar && quillRef && (
    <div id={toolbarId}>
      {addTagButton}
      {isHtml && <button className="ql-bold" type={'button'}></button>}
      {isHtml && <button className="ql-italic" type={'button'}></button>}
      {isHtml && <button className="ql-underline" type={'button'}></button>}
      {isHtml && <button className="ql-strike" type={'button'}></button>}
    </div>
  );

  const editorComponent = (
    <ReactQuill
      theme="snow"
      className={isHtml ? 'editor-html' : ''}
      value={content}
      onChange={(value, _delta, _source, editor) => {
        setContent(value);
        if (isHtml) {
          onContentUpdate(toStoredHtml(value));
        } else {
          onContentUpdate(toStoredText(editor.getHTML()));
        }
      }}
      placeholder={placeholder || ''}
      modules={modules}
      ref={quillRef}
      formats={formats}
      bounds={'.app'}
    />
  );

  return (
    <>
      {isHtml ? (
        <>
          {' '}
          {toolBar} {editorComponent}
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showToolbar && <div style={{ margin: 4, marginLeft: 0, width: '60px' }}>{toolBar}</div>}
          <div style={{ width: '100%' }}>{editorComponent}</div>
        </div>
      )}
    </>
  );
}
